import Service from '@ember/service';
import { ensureSafeComponent } from '@embroider/util';
import { getComponentTemplate, setComponentTemplate } from '@ember/component';
import type { ComponentLike } from '@glint/template';
import type { TemplateFactory } from '@glimmer/interfaces';
import loader from '../../runtime/precompiled-extension-loader.ts';
import defaultModules from '../../runtime/default-modules.ts';

type PrecompileFn = (
  source: string,
  options?: { strictMode?: boolean; locals?: string[]; moduleName?: string }
) => string;

// A module exposed to pre-compiled extensions: either a component or a record
// of named exports.
type ExtensionModule = unknown;

interface ComponentImport {
  component: () => unknown;
  template: () => unknown;
}

/**
 * Exposes host app modules to pre-compiled extensions.
 *
 * The host passes all modules to `setupImportMaps()`, which merges them with the
 * Ember runtime modules from `defaultModules` into a PRIVATE registry and then
 * locks it. The loader's es-module-shims hooks resolve extension imports against this
 * registry. There is no public, mutable module map, so the set of modules
 * available to extensions cannot be read or extended from outside the addon.
 */
export default class ImportMapService extends Service {
  initialized: boolean = false;

  // Private registry of everything extensions may import (Ember runtime modules
  // + host-provided modules). Frozen once setupImportMaps() runs.
  private extensionDependencies: Record<string, unknown> = {};

  // Once locked, the registry is frozen so no further modules can be added —
  // e.g. via the console or untrusted code.
  private locked = false;

  constructor() {
    super();

    // Register default modules that all extensions can use.
    this.registerExtensionDependencies(defaultModules.modules);

    // Register the dependency getter the loader uses to resolve extension
    // imports. The getter lives in module scope inside the loader (not on
    // globalThis), so only the addon can reach it.
    loader.setDependencyGetter((moduleName: string) =>
      this.getExtensionDependency(moduleName)
    );
  }

  /**
   * Prepare a host component (component + template) so it can be exposed to
   * extensions. The result is passed to `setupImportMaps()` under a module name.
   */
  async importComponent(
    componentImport: ComponentImport
  ): Promise<ExtensionModule> {
    const importedComponent = (await componentImport.component()) as {
      default: ComponentLike;
    };
    const importedTemplate = (await componentImport.template()) as {
      default: TemplateFactory;
    };

    const component = ensureSafeComponent(importedComponent.default, this);

    if (!getComponentTemplate(component)) {
      setComponentTemplate(importedTemplate.default, component);
    }

    return component;
  }

  /**
   * Register the host app's runtime template compiler.
   *
   * Pre-compiled extensions call `precompileTemplate` at runtime. Ember's own
   * implementation throws (build-time only), so the framework provides a runtime
   * version backed by the compiler the host passes here. Passing the host's own
   * `precompile` guarantees the produced wire format matches the glimmer runtime.
   *
   * @example
   * import { precompile } from 'ember-source/dist/ember-template-compiler.js';
   * importMap.registerTemplateCompiler(precompile);
   *
   * @param precompile - The `precompile` function from the host's ember-source
   */
  registerTemplateCompiler(precompile: PrecompileFn): void {
    defaultModules.setTemplateCompiler(precompile);
  }

  /**
   * Configure the modules available to extensions and lock the registry.
   *
   * Call once, with every module the host wants to expose. After this, the
   * registry is frozen: no further modules can be added (by the host, the
   * console, or extension code).
   *
   * @param modules - Map of module name to its exports (or a component)
   */
  setupImportMaps(modules: Record<string, ExtensionModule> = {}): void {
    if (this.locked) {
      return;
    }

    this.registerExtensionDependencies(modules);

    // Lock the registry. Object.freeze blocks adding keys even if `this` is
    // reached directly (e.g. via the console); the registration methods also
    // guard with assertNotLocked() for a clearer error.
    Object.freeze(this.extensionDependencies);
    this.locked = true;
    this.initialized = true;
  }

  private registerExtensionDependencies(
    dependencies: Record<string, unknown>
  ): void {
    this.assertNotLocked();
    Object.assign(this.extensionDependencies, dependencies);
  }

  private assertNotLocked(): void {
    if (this.locked) {
      throw new Error(
        'Extension modules are locked after setupImportMaps() and cannot be modified.'
      );
    }
  }

  private getExtensionDependency(moduleName: string): unknown {
    return this.extensionDependencies[moduleName];
  }
}
